import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Format percentage
export function formatPercentage(value, showSign = true) {
  const formatted = Math.abs(value).toFixed(1);
  if (showSign && value !== 0) {
    return value > 0 ? `+${formatted}%` : `-${formatted}%`;
  }
  return `${formatted}%`;
}

// Format large numbers
export function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

// Get currency flag emoji
export function getCurrencyFlag(currency) {
  const flags = {
    USD: '🇺🇸',
    EUR: '🇪🇺',
    GBP: '🇬🇧',
    JPY: '🇯🇵',
    AUD: '🇦🇺',
    CAD: '🇨🇦',
    CHF: '🇨🇭',
    CNY: '🇨🇳',
    NZD: '🇳🇿',
    SEK: '🇸🇪',
  };
  return flags[currency] || '🌐';
}

// Get impact color class
export function getImpactClass(impact) {
  switch (impact?.toLowerCase()) {
    case 'high':
      return 'impact-high';
    case 'medium':
      return 'impact-medium';
    case 'low':
    default:
      return 'impact-low';
  }
}

// Get premium tier gradient
export function getTierClass(tier) {
  switch (tier?.toLowerCase()) {
    case 'diamond':
      return 'tier-diamond';
    case 'platinum':
      return 'tier-platinum';
    case 'gold':
      return 'tier-gold';
    default:
      return 'bg-muted';
  }
}

// Truncate text
export function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

// Format date
export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format time
export function formatTime(timeString) {
  return timeString;
}

// Get relative time
export function getRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateString);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Generate broker score between 0 and 5.5 based on broker ID
export function generateBrokerScore(brokerId) {
  // Use a simple hash of the broker id to generate a consistent "random" score
  let hash = 0;
  const str = brokerId || 'default';
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  // Convert to 0-5.5 range
  return Math.abs(hash % 550) / 100;
}

// Generate trust score between 0 and 5.7 based on broker ID (different hash)
export function generateTrustScore(brokerId) {
  // Use a different hash calculation for trust score
  let hash = 0;
  const str = (brokerId || 'default') + '_trust';
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 7) - hash) + char;
    hash = hash & hash;
  }
  // Convert to 0-5.7 range
  return Math.abs(hash % 570) / 100;
}
